import React, { useEffect, useMemo, useState } from 'react';
import type { Ingredient, InstructionStep, Language, PlannedMeal, Recipe } from '../types';
import ClockIcon from './icons/ClockIcon';
import UsersIcon from './icons/UsersIcon';
import HamIcon from './icons/HamIcon';
import LeafIcon from './icons/LeafIcon';
import CartIcon from './icons/CartIcon';
import PencilIcon from './icons/PencilIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import XIcon from './icons/XIcon';
import SmartUnitCapsule from './SmartUnitCapsule';
import CookingIngredientRow from './CookingIngredientRow';
import CookingQuantityCapsule from './CookingQuantityCapsule';
import NutritionFactsPanel from './NutritionFactsPanel';
import { getTranslation, isRTL } from '../utils/translations';
import { getRecipeGalleryImageUrls } from '../utils/recipeMedia';
import RecipeImageGallery from './RecipeImageGallery';
import { getRecipeCategoryTranslationKey, RECIPE_CATEGORIES } from '../utils/recipeCategories';
import { sanitizeExternalHref } from '../utils/safeUrl';
import { matchIngredientsInStep } from '../utils/ingredientMatching';
import { getRecipeStruckIngredientKey } from '../utils/cookingSessionSteps';

function getIngredientRowKey(section: string, name: string, unit: string): string {
    return `${section}|${name}|${unit}`;
}

const CheckIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
);

const getDomain = (url: string) => {
    try {
        const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
        return urlObj.hostname.replace(/^www\./, '');
    } catch {
        return url;
    }
};

interface FutureInfo {
    days: string[];
    totalServings: number;
    totalMeals: number;
}

interface ActiveCookingSessionScreenProps {
    mealRecipe: PlannedMeal & { recipe: Recipe };
    language?: Language;
    isHidden?: boolean;
    servings: number;
    onUpdateServings: (newServings: number) => void;
    struckIngredients: Set<string>;
    onToggleStruck: (key: string) => void;
    completedSteps: Set<string>;
    onToggleStepComplete: (key: string) => void;
    isRecipeDone?: boolean;
    onMarkRecipeDone?: () => void;
    onAddIngredientsToCart?: (ingredients: Ingredient[], recipeName: string) => Promise<void>;
    onViewRecipe?: (recipeId: string) => void;
    onBack: () => void;
    futureInfo?: FutureInfo | null;
    sessionDateString?: string;
    showMealServingsContext?: boolean;
}

const ActiveCookingSessionScreen: React.FC<ActiveCookingSessionScreenProps> = ({
    mealRecipe,
    language,
    isHidden = false,
    servings,
    onUpdateServings,
    struckIngredients,
    onToggleStruck,
    completedSteps,
    onToggleStepComplete,
    isRecipeDone = false,
    onMarkRecipeDone,
    onAddIngredientsToCart,
    onViewRecipe,
    onBack,
    futureInfo,
    sessionDateString,
    showMealServingsContext = false,
}) => {
    const t = (key: any) => getTranslation(language, key);
    const rtl = isRTL(language);
    const recipe = mealRecipe.recipe;

    const [isHeroCollapsed, setIsHeroCollapsed] = useState(false);
    const [collapsedIngSections, setCollapsedIngSections] = useState<Set<string>>(new Set());
    const [collapsedPrepSections, setCollapsedPrepSections] = useState<Set<string>>(new Set());
    const [isNutritionCollapsed, setIsNutritionCollapsed] = useState(false);
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
    const [isAddingToCart, setIsAddingToCart] = useState(false);

    const activeNutritionFacts = useMemo(() => {
        const userLang = language || 'en';
        const sourceLang = recipe.originLanguage || 'en';
        if (userLang === sourceLang) return recipe.nutritionFactsPer100g;
        return recipe.englishTranslation?.nutritionFactsPer100g || recipe.nutritionFactsPer100g;
    }, [recipe, language]);

    const categoryLabel = recipe.category
        ? t(getRecipeCategoryTranslationKey(recipe.category) as Parameters<typeof getTranslation>[1])
        : null;

    const renderCategoryPill = () => categoryLabel ? (
        <div className="flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-full text-xs font-black text-slate-500 dark:text-slate-400 cursor-default">
            <span>{categoryLabel}</span>
        </div>
    ) : null;

    const groupedIngredients = useMemo(() => {
        const factor = servings / (recipe.servings || 1);
        const groups: Record<string, Ingredient[]> = {};
        recipe.ingredients.forEach((ing) => {
            const section = ing.section || t('ingredients');
            if (!groups[section]) groups[section] = [];
            groups[section].push({ ...ing, quantity: ing.quantity * factor });
        });
        return Object.entries(groups).map(([section, ings]) => {
            const sorted = [...ings].sort((a, b) => {
                if (a.isMeat && !b.isMeat) return -1;
                if (!a.isMeat && b.isMeat) return 1;
                return a.name.localeCompare(b.name);
            });
            return [section, sorted] as [string, Ingredient[]];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recipe, servings, t]);

    const groupedInstructions = useMemo(() => {
        const instructions = recipe.instructions;
        let rawSteps: InstructionStep[] = [];
        if (typeof instructions === 'string') {
            rawSteps = instructions.split('\n').map((s) => s.trim()).filter(Boolean)
                .map((s, i) => ({ id: `s-${i}`, type: 'step' as const, content: s.replace(/^\d+[\.\)\-\s]+/, '').trim() }));
        } else {
            rawSteps = instructions as InstructionStep[];
        }
        const sections: { header: string; steps: InstructionStep[] }[] = [];
        let current: { header: string; steps: InstructionStep[] } | null = null;
        rawSteps.forEach((s) => {
            if (s.type === 'header') {
                current = { header: s.content, steps: [] };
                sections.push(current);
            } else {
                if (!current) { current = { header: t('prepTab'), steps: [] }; sections.push(current); }
                current.steps.push(s);
            }
        });
        return sections;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recipe, t]);

    // Initialize collapsed sections once per recipe (ingredients collapsed, first prep section open)
    useEffect(() => {
        setCollapsedIngSections(new Set(groupedIngredients.map(([name]) => name)));
        setCollapsedPrepSections(new Set(groupedInstructions.map((s) => s.header).slice(1)));
        setIsHeroCollapsed(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recipe.id]);

    // Reset selection when leaving this screen state changes
    useEffect(() => {
        setIsSelectionMode(false);
        setSelectedKeys(new Set());
    }, [recipe.id]);

    const toggleSelection = (key: string) => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const toggleSelectKeys = (keys: string[]) => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (keys.length > 0 && keys.every((k) => next.has(k))) keys.forEach((k) => next.delete(k));
            else keys.forEach((k) => next.add(k));
            return next;
        });
    };

    const toggleIngSection = (name: string) => {
        setCollapsedIngSections((prev) => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name); else next.add(name);
            return next;
        });
    };

    const togglePrepSection = (name: string) => {
        setCollapsedPrepSections((prev) => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name); else next.add(name);
            return next;
        });
    };

    const stepKey = (sectionIdx: number, stepIdx: number) => `${recipe.id}:${sectionIdx}-${stepIdx}`;

    const getStepIngredients = (stepText: string, ingredients: Ingredient[], englishIngredients?: ReadonlyArray<{ name?: string }>) =>
        matchIngredientsInStep(stepText, ingredients, { englishIngredients });

    const allSelectableKeys = useMemo(
        () => groupedIngredients.flatMap(([section, ings]) =>
            ings.map((ing) => getIngredientRowKey(section, ing.name, ing.unit))
        ),
        [groupedIngredients],
    );
    const allSelected = allSelectableKeys.length > 0 && allSelectableKeys.every((k) => selectedKeys.has(k));

    const toggleSelectionMode = () => {
        setIsSelectionMode((prev) => !prev);
        setSelectedKeys(new Set());
    };

    const handleAddToCart = async () => {
        if (!onAddIngredientsToCart || selectedKeys.size === 0) return;
        setIsAddingToCart(true);
        try {
            const items = groupedIngredients
                .flatMap(([section, ings]) => ings.map((ing) => ({ section, ing })))
                .filter(({ section, ing }) => selectedKeys.has(getIngredientRowKey(section, ing.name, ing.unit)))
                .map(({ ing }) => ing)
                .map((ing) => ({
                    ...ing,
                    sources: [{
                        originName: recipe.name,
                        originType: 'day' as const,
                        originId: sessionDateString,
                        quantity: ing.quantity,
                    }],
                }));
            if (items.length > 0) await onAddIngredientsToCart(items, recipe.name);
            setIsSelectionMode(false);
            setSelectedKeys(new Set());
        } catch (error) {
            console.error('Failed to add to cart', error);
        } finally {
            setIsAddingToCart(false);
        }
    };

    const renderSelectionHeader = () => (
        <div className="flex items-center justify-between px-2">
            <h3 className="text-2xl font-black text-slate-800 dark:text-slate-100 tracking-tight">{t('ingredients')}</h3>
            <div className="flex items-center gap-2">
                {isSelectionMode && (
                    <button
                        onClick={() => toggleSelectKeys(allSelectableKeys)}
                        className="text-[10px] font-black uppercase tracking-widest text-brand-orange hover:text-orange-700 dark:hover:text-orange-300 transition-colors px-2 py-1"
                    >
                        {allSelected ? t('deselectAll') : t('selectAll')}
                    </button>
                )}
                {onAddIngredientsToCart && (
                    <button
                        onClick={toggleSelectionMode}
                        className={`p-2.5 rounded-full transition-all shadow-sm ${isSelectionMode ? 'bg-orange-50 dark:bg-orange-900/30 text-brand-orange ring-2 ring-brand-orange/20' : 'bg-white dark:bg-slate-800 text-slate-400 dark:text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
                        title={isSelectionMode ? t('cancel') : t('addToActive')}
                    >
                        {isSelectionMode ? <XIcon className="w-5 h-5" /> : <CartIcon className="w-5 h-5" />}
                    </button>
                )}
            </div>
        </div>
    );

    const filteredLinks = recipe.links?.filter((l) => l.trim() !== '') || [];
    const galleryImageUrls = useMemo(() => getRecipeGalleryImageUrls(recipe), [recipe.imageUrl, recipe.imageUrls]);

    return (
        <div className={`fixed inset-0 bg-bg-cream dark:bg-slate-950 z-50 flex-col font-rubik overflow-hidden transition-colors duration-200 ${isHidden ? 'hidden' : 'flex'}`} dir={rtl ? 'rtl' : 'ltr'}>
            {/* Back to Menu */}
            <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-stone-200 dark:border-slate-800 flex-shrink-0 px-4">
                <div className="max-w-2xl mx-auto">
                    <button
                        onClick={onBack}
                        className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 font-medium py-4 flex items-center gap-1.5 transition-colors"
                    >
                        <span>{rtl ? '›' : '‹'}</span>
                        <span>{t('backToMenu')}</span>
                    </button>
                </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-grow overflow-y-auto scrollbar-none">
                <div className="max-w-2xl mx-auto px-4 py-8 pb-32">
                    <div key={recipe.id} className="animate-fade-in">
                        {galleryImageUrls.length > 0 && (
                            <RecipeImageGallery
                                urls={galleryImageUrls}
                                alt={recipe.name}
                                language={language}
                                variant="compact"
                                className="mb-6"
                            />
                        )}
                        {/* Hero Card */}
                        {isHeroCollapsed ? (
                            <div
                                className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-sm p-4 px-5 sm:px-6 border border-stone-100 dark:border-slate-800 mb-8 flex flex-col sm:flex-row items-center justify-between gap-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group relative"
                                onClick={() => setIsHeroCollapsed(false)}
                            >
                                <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
                                    <div className={`p-2.5 rounded-2xl flex-shrink-0 ${recipe.ingredients.some((i) => i.isMeat) ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-500' : 'bg-brand-sage/5 dark:bg-brand-sage/10 text-brand-sage'}`}>
                                        {recipe.ingredients.some((i) => i.isMeat) ? <HamIcon className="w-6 h-6" /> : <LeafIcon className="w-6 h-6" />}
                                    </div>
                                    <h2 className="text-lg sm:text-xl font-nunito font-black text-brand-sage tracking-tight line-clamp-2 text-right w-full">
                                        {recipe.name}
                                    </h2>
                                    <div className="sm:hidden absolute top-4 left-4 p-1.5 bg-slate-50 dark:bg-slate-800 rounded-full">
                                        <ChevronDownIcon className="w-5 h-5 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300 transition-colors" />
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto justify-center sm:justify-end">
                                    <div className="flex items-center flex-wrap justify-center sm:justify-end gap-2 sm:gap-3" onClick={(e) => e.stopPropagation()}>
                                        {renderCategoryPill()}
                                        <div className="flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-full text-xs font-black text-slate-500 dark:text-slate-400 cursor-default">
                                            <ClockIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                            <span>{recipe.prepTime || 20} {t('mins')}</span>
                                        </div>
                                        <div className="flex items-center gap-1 sm:gap-2 px-1.5 sm:px-2 py-1 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-full cursor-default">
                                            <UsersIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 dark:text-slate-500 ml-1.5 sm:ml-2" />
                                            <SmartUnitCapsule
                                                value={servings}
                                                unit="servings"
                                                onChange={onUpdateServings}
                                                language={language}
                                                availableUnits={['servings']}
                                                step={1}
                                                min={1}
                                            />
                                        </div>
                                    </div>
                                    <div className="hidden sm:block p-1.5 bg-slate-50 dark:bg-slate-800 rounded-full">
                                        <ChevronDownIcon className="w-5 h-5 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300 transition-colors" />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white dark:bg-slate-900 rounded-[3rem] shadow-sm p-8 text-center border border-stone-100 dark:border-slate-800 mb-8 relative">
                                <button
                                    className="absolute top-6 right-6 p-2 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 bg-slate-50 dark:bg-slate-800 rounded-full transition-colors"
                                    onClick={() => setIsHeroCollapsed(true)}
                                >
                                    <ChevronDownIcon className="w-5 h-5 rotate-180" />
                                </button>
                                <div className="mb-6 flex justify-center">
                                    <div className={`p-6 rounded-[2.5rem] shadow-xl shadow-opacity-10 ${recipe.ingredients.some((i) => i.isMeat) ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-500 dark:text-rose-400' : 'bg-brand-sage/5 dark:bg-brand-sage/10 text-brand-sage dark:text-brand-sage'}`}>
                                        {recipe.ingredients.some((i) => i.isMeat) ? <HamIcon className="w-16 h-16" /> : <LeafIcon className="w-16 h-16" />}
                                    </div>
                                </div>
                                <h2 className="text-4xl font-nunito font-black text-brand-sage tracking-tight mb-4">
                                    {recipe.name}
                                </h2>
                                <div className="flex flex-wrap justify-center items-center gap-3 mb-6">
                                    {renderCategoryPill()}
                                    <div className="flex items-center gap-1.5 px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-full text-xs font-black text-slate-500 dark:text-slate-400">
                                        <ClockIcon className="w-4 h-4" />
                                        <span>{recipe.prepTime || 20} {t('mins')}</span>
                                    </div>
                                    <div className="flex items-center gap-2 px-2 py-1 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-full">
                                        <UsersIcon className="w-4 h-4 text-slate-400 dark:text-slate-500 ml-2" />
                                        <div className="flex flex-col items-start py-0.5">
                                            {showMealServingsContext && (
                                                <span className="text-[10px] font-black uppercase tracking-wider text-brand-sage leading-none mb-1">
                                                    {t('servingsPerMeal')}
                                                </span>
                                            )}
                                            <SmartUnitCapsule
                                                value={servings}
                                                unit="servings"
                                                onChange={onUpdateServings}
                                                language={language}
                                                availableUnits={['servings']}
                                                step={1}
                                                min={1}
                                            />
                                        </div>
                                    </div>
                                    {showMealServingsContext && recipe.servings && recipe.servings !== servings && (
                                        <div className="w-full text-center text-[11px] font-semibold text-slate-500 dark:text-slate-400 px-4">
                                            {t('recipeDefaultServingsHint').replace('{count}', String(recipe.servings))}
                                        </div>
                                    )}
                                </div>
                                {futureInfo && (
                                    <div className="mb-6 mx-auto max-w-md p-4 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-900/30 rounded-3xl text-center flex flex-col items-center gap-3">
                                        <div className="flex items-center gap-2 text-xs font-semibold text-amber-800 dark:text-amber-400">
                                            <span>💡</span>
                                            <span>
                                                {t('futureMealsPlannedHint')
                                                    .replace('{recipe}', recipe.name)
                                                    .replace('{days}', futureInfo.days.join(', '))
                                                    .replace('{totalServings}', String(futureInfo.totalServings))
                                                    .replace('{totalMeals}', String(futureInfo.totalMeals))}
                                            </span>
                                        </div>
                                        {servings !== futureInfo.totalServings && (
                                            <button
                                                onClick={() => onUpdateServings(futureInfo.totalServings)}
                                                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-colors shadow-sm"
                                            >
                                                {t('scaleToTotalServings').replace('{count}', String(futureInfo.totalServings))}
                                            </button>
                                        )}
                                    </div>
                                )}
                                {onViewRecipe && (
                                    <button
                                        onClick={() => onViewRecipe(recipe.id)}
                                        className="inline-flex items-center gap-2 px-8 py-3 bg-brand-orange text-white rounded-2xl shadow-lg shadow-brand-orange/20 font-black text-xs uppercase tracking-widest hover:opacity-90 active:scale-95 transition-all"
                                    >
                                        <PencilIcon className="w-4 h-4" />
                                        {t('goToRecipe')}
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Section 1: Ingredients Needed */}
                        <div className="mb-10 space-y-6">
                            {renderSelectionHeader()}
                            {groupedIngredients.map(([section, ings]) => {
                                const isCollapsed = collapsedIngSections.has(section);
                                const translatedSection = t(section.toLowerCase());
                                const displaySection = translatedSection !== section.toLowerCase() ? translatedSection : section;
                                const sectionKeys = ings.map((ing) => getIngredientRowKey(section, ing.name, ing.unit));
                                const sectionFullySelected = sectionKeys.length > 0 && sectionKeys.every((k) => selectedKeys.has(k));
                                return (
                                    <div key={section} className="bg-white dark:bg-slate-900 rounded-[2rem] border border-stone-100 dark:border-slate-800 overflow-hidden shadow-sm transition-all duration-300">
                                        <div className="flex justify-between items-center p-5 cursor-pointer group hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" onClick={() => toggleIngSection(section)}>
                                            <div className="flex items-center gap-3 flex-grow min-w-0" onClick={(e) => e.stopPropagation()}>
                                                <div className={`transition-transform duration-300 ${isCollapsed ? 'ltr:-rotate-90 rtl:rotate-90' : ''}`} onClick={() => toggleIngSection(section)}>
                                                    <ChevronDownIcon className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                                                </div>
                                                <h4 className="text-xs font-black text-brand-orange uppercase tracking-[0.2em] truncate" onClick={() => toggleIngSection(section)}>{displaySection}</h4>
                                            </div>
                                            {isSelectionMode && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); toggleSelectKeys(sectionKeys); }}
                                                    className="shrink-0 text-[9px] font-black uppercase tracking-widest text-brand-orange hover:text-orange-700 dark:hover:text-orange-300 transition-colors px-2 py-1"
                                                >
                                                    {sectionFullySelected ? t('deselectAll') : t('selectAll')}
                                                </button>
                                            )}
                                        </div>
                                        {!isCollapsed && (
                                            <div className="border-t border-slate-50 dark:border-slate-800 animate-fade-in divide-y divide-slate-50 dark:divide-slate-800">
                                                {ings.map((ing) => {
                                                    const rowKey = getIngredientRowKey(section, ing.name, ing.unit);
                                                    const strikeKey = getRecipeStruckIngredientKey(recipe.id, section, ing.name, ing.unit);
                                                    return (
                                                        <CookingIngredientRow
                                                            key={rowKey}
                                                            ingredient={ing}
                                                            isStruck={struckIngredients.has(strikeKey)}
                                                            isSelectionMode={isSelectionMode}
                                                            isSelected={selectedKeys.has(rowKey)}
                                                            t={t}
                                                            rtl={rtl}
                                                            onToggle={() => (isSelectionMode ? toggleSelection(rowKey) : onToggleStruck(strikeKey))}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Instructions (grouped sections + step completion) */}
                        <div className="mb-10 space-y-6">
                            <div className="px-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <h3 className="text-2xl font-black text-slate-800 dark:text-slate-100 tracking-tight flex items-center gap-2">
                                    <ClockIcon className="w-6 h-6 text-brand-sage" />
                                    {t('instructions')}
                                </h3>
                                {onMarkRecipeDone && (
                                    isRecipeDone ? (
                                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-brand-sage/10 text-brand-sage border border-brand-sage/20 text-xs font-black uppercase tracking-widest">
                                            <CheckIcon className="w-4 h-4" />
                                            {t('recipeDone')}
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={onMarkRecipeDone}
                                            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-2xl bg-brand-sage text-white text-xs font-black uppercase tracking-widest shadow-lg shadow-brand-sage/20 hover:opacity-90 active:scale-95 transition-all"
                                        >
                                            <CheckIcon className="w-4 h-4" />
                                            {t('markRecipeDone')}
                                        </button>
                                    )
                                )}
                            </div>
                            {groupedInstructions.map((section, sIdx) => {
                                const isCollapsed = collapsedPrepSections.has(section.header);
                                const translatedHeader = t(section.header.toLowerCase());
                                const displayHeader = translatedHeader !== section.header.toLowerCase() ? translatedHeader : section.header;
                                const sectionDoneCount = section.steps.filter((_, idx) => completedSteps.has(stepKey(sIdx, idx))).length;
                                const sectionComplete = section.steps.length > 0 && sectionDoneCount === section.steps.length;
                                return (
                                    <div key={section.header} className="bg-white dark:bg-slate-900 rounded-[2rem] border border-stone-100 dark:border-slate-800 overflow-hidden shadow-sm transition-all duration-300">
                                        <div className="flex justify-between items-center p-5 cursor-pointer group hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" onClick={() => togglePrepSection(section.header)}>
                                            <div className="flex items-center gap-3 flex-grow min-w-0" onClick={(e) => e.stopPropagation()}>
                                                <div className={`transition-transform duration-300 ${isCollapsed ? 'ltr:-rotate-90 rtl:rotate-90' : ''}`} onClick={() => togglePrepSection(section.header)}>
                                                    <ChevronDownIcon className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                                                </div>
                                                <h4 className="text-xs font-black text-brand-sage uppercase tracking-[0.2em] truncate" onClick={() => togglePrepSection(section.header)}>{displayHeader}</h4>
                                            </div>
                                            <span className={`shrink-0 text-[10px] font-black tabular-nums px-2 py-0.5 rounded-full border transition-colors ${sectionComplete ? 'bg-brand-sage/10 text-brand-sage border-brand-sage/20' : 'bg-white dark:bg-slate-800 text-slate-400 dark:text-slate-500 border-slate-100 dark:border-slate-700'}`}>
                                                {sectionDoneCount}/{section.steps.length}
                                            </span>
                                        </div>
                                        {!isCollapsed && (
                                            <div className="border-t border-slate-50 dark:border-slate-800 animate-fade-in p-6 space-y-8">
                                                {section.steps.map((step, idx) => {
                                                    const stepIngredients = getStepIngredients(
                                                        step.content,
                                                        recipe.ingredients,
                                                        recipe.englishTranslation?.ingredients,
                                                    );
                                                    const key = stepKey(sIdx, idx);
                                                    const isDone = completedSteps.has(key);
                                                    return (
                                                        <div key={step.id || idx} className="flex gap-6 items-start group">
                                                            <button
                                                                type="button"
                                                                onClick={() => onToggleStepComplete(key)}
                                                                title={isDone ? t('markIncomplete') : t('markComplete')}
                                                                className={`flex-shrink-0 w-10 h-10 rounded-2xl flex items-center justify-center font-black text-lg border shadow-sm transition-all active:scale-95 ${isDone ? 'bg-brand-sage border-brand-sage text-white' : 'bg-brand-sage/5 dark:bg-brand-sage/10 text-brand-sage border-brand-sage/10 hover:bg-brand-sage/10 dark:hover:bg-brand-sage/20'}`}
                                                            >
                                                                {isDone ? <CheckIcon className="w-5 h-5" /> : (idx + 1)}
                                                            </button>
                                                            <div className="flex-grow">
                                                                <p className={`text-xl leading-relaxed font-medium pt-1 transition-all ${isDone ? 'line-through text-slate-300 dark:text-slate-600' : 'text-slate-700 dark:text-slate-200'}`}>
                                                                    {step.content}
                                                                </p>
                                                                {stepIngredients.length > 0 && (
                                                                    <div className={`flex flex-wrap gap-2 mt-3 transition-opacity ${isDone ? 'opacity-40' : ''}`}>
                                                                        {stepIngredients.map((ing) => {
                                                                            const scalingFactor = servings / (recipe.servings || 1);
                                                                            const qty = ing.quantity * scalingFactor;
                                                                            return (
                                                                                <div key={ing.name} className="inline-flex items-center px-2.5 py-1 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800/50 text-orange-700 dark:text-orange-300 text-xs font-bold">
                                                                                    <span className="opacity-70 ltr:mr-1 rtl:ml-1">{ing.name}:</span>
                                                                                    <CookingQuantityCapsule quantity={qty} unit={ing.unit} t={t} rtl={rtl} variant="inline" struck={isDone} />
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {activeNutritionFacts && (
                            <div className="mb-10 px-1">
                                <NutritionFactsPanel
                                    facts={activeNutritionFacts}
                                    language={language}
                                    collapsed={isNutritionCollapsed}
                                    onToggleCollapse={() => setIsNutritionCollapsed((prev) => !prev)}
                                />
                            </div>
                        )}

                        {/* Links */}
                        {filteredLinks.length > 0 && (
                            <div className="bg-slate-50 dark:bg-slate-900/50 rounded-[2.5rem] p-8 border border-stone-100 dark:border-slate-800 mt-8">
                                <h4 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-4 px-1">{t('links')}</h4>
                                <div className="flex flex-wrap gap-2">
                                    {filteredLinks.map((link, idx) => {
                                        const safeHref = sanitizeExternalHref(link);
                                        return safeHref ? (
                                            <a
                                                key={idx}
                                                href={safeHref}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-3 px-4 py-2.5 bg-white dark:bg-slate-800 border border-stone-200 dark:border-slate-700 rounded-xl text-sky-600 dark:text-sky-400 font-bold text-sm hover:shadow-md transition-all"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                                <span className="truncate max-w-[180px]">{getDomain(link)}</span>
                                            </a>
                                        ) : null;
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Floating Selection Bar */}
            {isSelectionMode && onAddIngredientsToCart && (
                <div className="absolute bottom-4 left-4 right-4 max-w-2xl mx-auto bg-slate-900 dark:bg-slate-800 text-white p-4 rounded-2xl shadow-2xl flex justify-between items-center z-[210] animate-fade-in border border-slate-700">
                    <span className="font-bold text-sm px-2">{selectedKeys.size} {t('selected')}</span>
                    <div className="flex gap-3">
                        <button onClick={toggleSelectionMode} className="px-4 py-2 text-slate-400 font-bold text-xs uppercase tracking-widest hover:text-white transition-colors">
                            {t('cancel')}
                        </button>
                        <button
                            onClick={handleAddToCart}
                            disabled={isAddingToCart || selectedKeys.size === 0}
                            className="bg-brand-orange text-white px-6 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest shadow-lg shadow-brand-orange/20 active:scale-95 transition-all disabled:opacity-50 disabled:shadow-none flex items-center justify-center gap-2"
                        >
                            {isAddingToCart ? (
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            ) : (
                                <CartIcon className="w-4 h-4" />
                            )}
                            {t('addToActive')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ActiveCookingSessionScreen;
